import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {PasswordStrengthValidation, PasswordStrengthValidationConfig} from "@/features/auth/passwordStrengthValidation";
import {TimeAccountDashboardQueryParams} from "@/shared/queryParams";
import {UserDialogHandler} from "./userDialog";
import {ReactIsland} from "@/shared/reactIsland";
import {
	mountPasswordStrengthIsland,
	type PasswordStrengthIslandCommands,
	type PasswordStrengthIslandProps,
} from "@/features/auth/passwordStrengthIsland";
import {FormUtil} from "@/shared/forms/formUtil";
import {
	mountAddUserSuccessModalIsland,
	type AddUserSuccessModalIslandCommands,
} from "./addUserSuccessModalIsland";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {Notify} from "@/shared/ui/notify";
import {SecurityStatus} from "@/shared/securityStatus";
import {ErrorResponse, User} from "@/shared/timr";

export enum AddUserMode {
	Password = "PASSWORD",
	Invite = "INVITE",
	TerminalQrCode = "TERMINAL_QR_CODE"
}

export interface AddUserDialogConfig {
	initialMode: AddUserMode;
	passwordStrengthValidation?: PasswordStrengthValidation;
}

export class AddUserDialogHandler {

	private static readonly ATTRIBUTE_ARIA_DISABLED = "aria-disabled";
	private static readonly CLASS_DISABLED = "disabled";

	private modal: BootstrapModal.Modal;
	private wizard: JQuery;
	private mode: AddUserMode;
	private isAsyncStep = false;

	private readonly passwordStrengthIsland?: ReactIsland<PasswordStrengthIslandProps, PasswordStrengthIslandCommands>;
	private readonly successModalIsland: ReactIsland<object, AddUserSuccessModalIslandCommands>;
	private readonly passwordStrengthValidation?: PasswordStrengthValidationConfig;

	constructor(config: AddUserDialogConfig) {
		this.initModal(config.initialMode);
		this.initWizard(config.initialMode);
		this.initListener();
		// Mount AFTER initWizard: jQuery-steps relocates the form content on init, so the React
		// root must land in the final DOM (mounting earlier leaves it in a detached, dead copy
		// that renders once but never updates).
		this.passwordStrengthIsland = mountPasswordStrengthIsland("addUserPasswordStrengthIsland", config.passwordStrengthValidation);
		this.successModalIsland = mountAddUserSuccessModalIsland();
		this.passwordStrengthValidation = config.passwordStrengthValidation !== undefined ?
			new PasswordStrengthValidationConfig(config.passwordStrengthValidation) : undefined;
	}

	public open() {
		this.modal.show();
	}

	public setMode(mode: AddUserMode) {
		this.mode = mode;
		this.wizard.find(`input[type="radio"][name="addUserMode"][value="${mode}"]`).prop("checked", true);

		this.switchUserModeFields();
	}

	private initModal(initialAddUserMode: AddUserMode) {
		this.modal = new BootstrapModal.Modal({
			title: "user.addUserDialog.title",
			message: $("#addUserDialog"),
			footer: false,
			css: {
				content: "test-css"
			},
			onClose: () => {
				this.setMode(initialAddUserMode);
				this.resetWizard();
				this.resetFields();
			}
		});
	}

	private initWizard(initialAddUserMode: AddUserMode) {
		this.wizard = $("#addUserDialog > form").steps({
			headerTag: "h6",
			bodyTag: "fieldset",
			transitionEffect: "fade",
			cssClass: "wizard modal-wizard",
			enableCancelButton: true,
			labels: {
				previous: messages["steps.previous"],
				next: messages["steps.next"],
				finish: messages["user.button.invite"],
				cancel: messages["dialog.cancel"]
			},
			onFinished: (event: Event) => {
				const form = $(event.target);
				const button = form.find(".actions > ul > li > a[href='#finish']");

				this.addUser(button);
			},
			onStepChanging: (event: Event, currentIndex: number, newIndex: number) => {
				// isAsyncStep is for a workaround for async validation request
				// https://stackoverflow.com/a/51191180

				if (this.isAsyncStep) {
					this.isAsyncStep = false;
					return true;
				}
				if (currentIndex < newIndex) {
					const form = $(event.target);
					const button = form.find(`a[href="#next"]`);

					Button.setLoading(button);
					if (currentIndex === 0) {
						const passwordMode = this.mode === AddUserMode.Password;
						if (passwordMode) {
							this.wizard.find(`input[name="email"]`).val("");
						}
						this.firstStepValid().done(() => {
							this.isAsyncStep = true;
							this.wizard.steps("next");

							const emailAddress = passwordMode ? "" : form.find("#addUserDialogEmail").val();
							form.find("#addUserDialogLogin").val(emailAddress);
							this.updatePrimaryButtonText();
							return true;
						}).fail(() => {
							Button.resetLoading(button);
							return false;
						});
					}
					if (currentIndex === 1) {
						this.secondStepValid().done(() => {
							this.isAsyncStep = true;
							this.wizard.steps("next");
							return true;
						}).fail(() => {
							Button.resetLoading(button);
							return false;
						});
					}
				} else {
					return true;
				}
			},
			onStepChanged: (event: Event, currentIndex: number, priorIndex: number) => {
				if (priorIndex < currentIndex) {
					const form = $(event.target);
					const button = form.find(`a[href="#next"]`);
					Button.resetLoading(button);
				}
			},
			onCanceled: () => {
				this.modal.close();
			}
		});
		this.setMode(initialAddUserMode);
	}

	private initListener() {
		this.wizard.find(`input[type="radio"][name="addUserMode"]`).change(() => {
			this.switchUserModeFields();
		});
		if (globals.projectTimeEnabled) {
			this.wizard.find("#addUserDialogAdmin").change(e => {
				const adminChecked = $(e.target).is(":checked");
				const addUserTaskManager = this.wizard.find(`#addUserDialogTaskManager`);

				Input.toggleCheckbox(addUserTaskManager, adminChecked);
				if (adminChecked) {
					addUserTaskManager.prop("checked", true);
				}
			});
		}

		this.wizard.find("#addUserDialogTerminalQrSendQrCode").change(e => {
			const sendQrCodeEmail = $(e.target).is(":checked");
			this.wizard.find(".addUserDialogEmailField").toggle(sendQrCodeEmail);
		});

		const overallTaskLeaderCheckbox = this.wizard.find("#addUserDialogOverallTaskLeader");
		overallTaskLeaderCheckbox.on("change", () => {
			$("#addUserDialogTaskLeader")
				.prop("checked", true)
				.prop("disabled", overallTaskLeaderCheckbox.is(":checked"));
		});
	}

	private resetWizard() {
		this.wizard.steps("reset");
	}

	private resetFields() {
		this.wizard.find(`input:not([type="radio"])`).prop("disabled", false);
		this.wizard.find(`input[type="text"]`).val("");
		this.wizard.find(`input[type="password"]`).val("");
		this.wizard.find(`input[type="checkbox"]`)
			.not("#addUserDialogTerminalQrSendQrCode")
			.prop("checked", false);
		this.wizard.find("#addUserDialogTerminalQrSendQrCode").prop("checked", true);
	}

	private switchUserModeFields() {
		this.mode = this.wizard.find(`input[type="radio"][name="addUserMode"]:checked`).val() as AddUserMode;

		const sendQrCode = this.wizard.find("#addUserDialogTerminalQrSendQrCode").is(":checked");
		this.wizard.find(".addUserDialogEmailField").toggle(this.mode === AddUserMode.Invite
			|| this.mode === AddUserMode.TerminalQrCode && sendQrCode);
		this.wizard.find(".addUserDialogPasswordFields").toggle(this.mode === AddUserMode.Password);
		this.wizard.find(".addUserDialogTerminalQrCodeField").toggle(this.mode === AddUserMode.TerminalQrCode);
		this.wizard.find(".addUserDialogLoginField").toggle(this.mode !== AddUserMode.TerminalQrCode);
		this.updateSecondPageTitle();
		this.clearFieldsOnModeSwitch();

		if (this.mode === AddUserMode.Password) {
			this.initPasswordStrengthValidationListeners();
		} else {
			this.enableNextButton();
		}
	}

	/**
	 * Updates the form section header and step indicator circle label at the top of the wizard (#steps-uid-0-t-1)
	 * It's done by jQuery, because there is no method to do this directly in the jQuery steps framework
	 * @private
	 */
	private updateSecondPageTitle() {
		const secondPageTitle = messages[this.mode !== AddUserMode.TerminalQrCode ? "user.addUserDialog.step.loginAndName" : "user.addUserDialog.step.name"];
		this.wizard.find(".addUserDialogSecondPageTitle").text(secondPageTitle);
		(this.wizard.find("a#steps-uid-0-t-1").contents()[1] as ChildNode).replaceWith(secondPageTitle);
	}

	private initPasswordStrengthValidationListeners() {
		const passwordField = this.getPasswordField();

		passwordField.on("input", (event) => {
			const target = event.target as HTMLInputElement;
			this.passwordStrengthIsland?.dispatchCommand("passwordChanged", target.value);
			this.validatePasswordStrength(target.value);
		});

		passwordField.focus();

		if (this.passwordStrengthValidation) {
			this.disableNextButton();
		} else {
			this.enableNextButton();
		}
	}

	private validatePasswordStrength(input: string) {
		if (!this.passwordStrengthValidation) {
			return;
		}

		// The checklist UI now lives in the React island; here we only gate the wizard's next button.
		this.updateNextButtonState(this.passwordStrengthValidation.validate(input).isValid);
	}

	private updateNextButtonState(enabled: boolean) {
		if (enabled) {
			this.enableNextButton();
		} else {
			this.disableNextButton();
		}
	}

	private updatePrimaryButtonText() {
		let saveLabel: string;
		if (this.mode === AddUserMode.Invite) {
			saveLabel = messages["user.button.invite"];
		} else if (this.mode === AddUserMode.TerminalQrCode && this.wizard.find("#addUserDialogTerminalQrSendQrCode").is(":checked")) {
			saveLabel = messages["user.button.sendQrCode"];
		} else {
			saveLabel = messages["dialog.save"];
		}
		this.wizard.find(`a[href="#finish"]`).text(saveLabel);
	}

	private clearFieldsOnModeSwitch() {
		this.wizard.find(`input[type="password"]`).val("");
		this.wizard.find(".steps li").removeClass("error");
		FormUtil.removeAllFeedback(this.wizard);
		this.getPasswordField().off("input");
		this.passwordStrengthIsland?.dispatchCommand("passwordChanged", "");
	}

	private getNextButton() {
		return this.wizard.find("a[href='#next']");
	}

	private getPasswordField() {
		return $("#addUserDialogPassword1");
	}

	private enableNextButton() {
		this.getNextButton().parent()
			.removeClass(AddUserDialogHandler.CLASS_DISABLED)
			.attr(AddUserDialogHandler.ATTRIBUTE_ARIA_DISABLED, "false");
	}

	private disableNextButton() {
		this.getNextButton().parent()
			.addClass(AddUserDialogHandler.CLASS_DISABLED)
			.attr(AddUserDialogHandler.ATTRIBUTE_ARIA_DISABLED, "true");
	}

	private firstStepValid(): JQuery.jqXHR<boolean> {
		const deferred: JQueryDeferred<boolean> = $.Deferred();
		const form = this.wizard.serializeArray();
		return api("admin/isEmailOrPasswordValid.json")
			.get(form)
			.done((valid: boolean) => {
				deferred.resolve(valid);
				FormUtil.removeAllFeedback(this.wizard);
			})
			.fail((response: ErrorResponse) => {
				deferred.reject();
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(this.wizard, response.responseJSON.fieldErrors);
				}
			});
	}

	private secondStepValid(): JQuery.jqXHR<boolean> {
		const deferred: JQueryDeferred<boolean> = $.Deferred();
		const form = this.wizard.serializeArray();
		return api("admin/isLoginValid.json")
			.get(form)
			.done((valid: boolean) => {
				deferred.resolve(valid);
				FormUtil.removeAllFeedback(this.wizard);
			})
			.fail((response: ErrorResponse) => {
				deferred.reject();
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(this.wizard, response.responseJSON.fieldErrors);
				}
			});
	}

	private showSuccessModal(user: User, showQrCode: boolean, showTimeAccountInit: boolean) {
		this.successModalIsland.dispatchCommand("show", {
			user: {id: user.id, name: user.fullname, login: user.login},
			showQrCode,
			showTimeAccountInit,
			initializeTimeAccountLink: TimeAccountDashboardQueryParams.buildLink({
				userFilter: user.id,
			}, "timeaccounts/timeAccountDashboard.html", "initializeTimeAccount"),
		});
	}

	private addUser(button: JQuery) {
		api("admin/addUser.json")
			.postForm(this.wizard)
			.done((data) => {
				SecurityStatus.update(data.securityStatus);
				this.modal.close();
				$(this).triggerHandler(UserDialogHandler.EVENT_SAVED_USER, data.user);

				const showQrCode = this.mode === AddUserMode.TerminalQrCode && !this.wizard.find("#addUserDialogTerminalQrSendQrCode").is(":checked");
				const showTimeAccountInit = SecurityStatus.isTimeAccountAllowed() && globals.workingTimeEnabled;

				if (showQrCode || showTimeAccountInit) {
					this.showSuccessModal(data.user, showQrCode, showTimeAccountInit);
				} else {
					Notify.success(messages.withArguments("user.addUserDialog.success", data.user.fullname));
				}
				return data;
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markWizardFieldErrors(this.wizard, response.responseJSON.fieldErrors);
				}
			})
			.always(() => {
				Button.resetLoading(button);
			});
	}
}
